/**
 * Cart drawer — "Pair with" carousel behaviours.
 *
 * Both elements are plain custom elements on purpose: the theme replaces the drawer footer's children
 * on every cart mutation (see CartDrawer._onCartChanged in theme.js), and custom elements re-upgrade
 * when the new nodes are inserted, so there is nothing to re-initialise by hand.
 */

/**
 * Drag-to-scroll for the card track.
 *
 * Touch and trackpad already scroll the overflow container natively, so only mouse pointers are
 * hijacked — hijacking touch would replace native momentum scrolling with a worse imitation.
 */
class CartPairCarousel extends HTMLElement {
  connectedCallback() {
    this._pointerId = null;
    this._isDragging = false;
    this._suppressClick = false;

    this.addEventListener('pointerdown', this._onPointerDown);
    this.addEventListener('click', this._onClick, { capture: true });

    // Cards contain links and images, so the browser's own drag-and-drop would take over a few pixels
    // into a swipe and fire pointercancel, stopping the scroll dead.
    this.addEventListener('dragstart', this._onDragStart);

    this._resizeObserver = new ResizeObserver(() => this._updateDraggableState());
    this._resizeObserver.observe(this);
    this._updateDraggableState();
  }

  disconnectedCallback() {
    this._resizeObserver?.disconnect();
    this._stopTracking();
  }

  get _isOverflowing() {
    return this.scrollWidth > this.clientWidth + 1;
  }

  _updateDraggableState = () => {
    this.classList.toggle('is-draggable', this._isOverflowing);
  };

  _onDragStart = (event) => {
    event.preventDefault();
  };

  _onPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0 || !this._isOverflowing) {
      return;
    }

    this._pointerId = event.pointerId;
    this._startX = event.clientX;
    this._startScrollLeft = this.scrollLeft;
    this._isDragging = false;

    // The rest of the gesture is tracked on the window rather than on this element. Binding the moves
    // here would end the swipe the moment the cursor left the track — including leaving the drawer
    // entirely — because an element only receives pointer events that land on it. Pointer capture is
    // not enough on its own either: it can only be claimed once the drag threshold is crossed, so a
    // fast flick escapes before it is set, and the drawer re-renders its footer on every cart
    // mutation, which drops the capture along with the old node.
    window.addEventListener('pointermove', this._onPointerMove, { passive: false });
    window.addEventListener('pointerup', this._onPointerUp);
    window.addEventListener('pointercancel', this._onPointerUp);
  };

  _stopTracking = () => {
    window.removeEventListener('pointermove', this._onPointerMove);
    window.removeEventListener('pointerup', this._onPointerUp);
    window.removeEventListener('pointercancel', this._onPointerUp);
  };

  _onPointerMove = (event) => {
    if (event.pointerId !== this._pointerId) {
      return;
    }

    const distance = event.clientX - this._startX;

    // Only take over once the intent is clearly a drag, so plain clicks on the card still work.
    if (!this._isDragging) {
      if (Math.abs(distance) < 5) {
        return;
      }

      this._isDragging = true;
      this.classList.add('is-dragging');
    }

    // Suppresses the text selection the drag would otherwise paint across the page.
    event.preventDefault();
    this.scrollLeft = this._startScrollLeft - distance;
  };

  _onPointerUp = (event) => {
    if (event.pointerId !== this._pointerId) {
      return;
    }

    if (this._isDragging) {
      // Stops the pointerup from becoming a click on the card that happened to be under the cursor.
      // The click lands immediately after this event, so disarming on the next task is enough — and
      // it is necessary, because a drag released outside the track is followed by no click at all and
      // the flag would otherwise swallow the next genuine one.
      this._suppressClick = true;
      setTimeout(() => {
        this._suppressClick = false;
      });
    }

    this._pointerId = null;
    this._isDragging = false;
    this.classList.remove('is-dragging');
    this._stopTracking();
  };

  _onClick = (event) => {
    if (!this._suppressClick) {
      return;
    }

    this._suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  };
}

if (!window.customElements.get('cart-pair-carousel')) {
  window.customElements.define('cart-pair-carousel', CartPairCarousel);
}

/**
 * Fills the remaining carousel slots from the Product Recommendations API.
 *
 * Fetches the cart drawer section back through the recommendations route, which is the only context
 * where Liquid's `recommendations` object is populated, then unwraps the cards it rendered into the
 * carousel. Deliberately does NOT reuse the theme's own `product-recommendations` element: on an empty
 * `related` response that one calls `section.remove()`, which inside the drawer would delete the whole
 * cart drawer section.
 */
class CartPairRecommendations extends HTMLElement {
  async connectedCallback() {
    if (this._isLoaded) {
      return;
    }

    this._isLoaded = true;

    const productId = this.getAttribute('product');
    const sectionId = this.closest('.shopify-section')?.id.replace('shopify-section-', '');

    if (!productId || !sectionId) {
      this._discard();
      return;
    }

    const params = new URLSearchParams({
      product_id: productId,
      limit: this.getAttribute('limit') || '4',
      intent: this.getAttribute('intent') || 'related',
      section_id: sectionId,
    });

    try {
      const response = await fetch(`${Shopify.routes.root}recommendations/products?${params}`, {
        priority: 'low',
      });

      if (!response.ok) {
        this._discard(`the recommendations route answered ${response.status}`);
        return;
      }

      const document_ = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fetched = document_.querySelector('cart-pair-recommendations');

      if (!fetched) {
        this._discard('the response did not contain a cart-pair-recommendations element');
        return;
      }

      if (fetched.childElementCount === 0) {
        this._discard(`Shopify returned no "${params.get('intent')}" recommendations for product ${productId}`);
        return;
      }

      this.replaceWith(...document.importNode(fetched, true).childNodes);
    } catch (error) {
      this._discard(`the request failed (${error})`);
    }
  }

  /**
   * Remove the placeholder, and the whole block with it when no card ended up being shown at all —
   * otherwise a bare heading and an empty carousel would be left behind.
   *
   * Removing the block is silent by design on the storefront, which makes "the Pair with section just
   * isn't there" impossible to tell apart from a Liquid problem. The warning names the reason so the
   * two cases are distinguishable from the console.
   */
  _discard(reason) {
    const block = this.closest('.cart-drawer__pair-with');
    const removesBlock = block && !block.querySelector('.cart-pair-item');

    if (reason) {
      console.warn(
        `[cart-pair-recommendations] No filler cards: ${reason}.` +
          (removesBlock ? ' Removing the whole "Pair with" block — it had no other cards.' : '')
      );
    }

    this.remove();

    if (removesBlock) {
      block.remove();
    }
  }
}

if (!window.customElements.get('cart-pair-recommendations')) {
  window.customElements.define('cart-pair-recommendations', CartPairRecommendations);
}
